import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";
import { Plus, Briefcase, Trash2, AlertCircle, BadgeEuro } from "lucide-react";
import { toast } from "sonner";

export default function RivenditoreExternalProducts() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sku: "", nome: "", brand: "", categoria: "", descrizione_short: "", immagini: [], prezzo: 0, stock_qta: 0 });

  const load = async () => {
    const r = await api.get("/external-products");
    setItems(r.data.items || []);
    setSummary(r.data.summary || null);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/external-products", form);
      toast.success("Prodotto esterno aggiunto");
      setOpen(false);
      setForm({ sku: "", nome: "", brand: "", categoria: "", descrizione_short: "", immagini: [], prezzo: 0, stock_qta: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il prodotto esterno?")) return;
    await api.delete(`/external-products/${id}`);
    toast.success("Eliminato");
    load();
  };

  const policyEnabled = summary?.policy_enabled;

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="riv-external-page">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline text-brand mb-2">Rivenditore</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">Prodotti complementari (brand esterni)</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Aggiungi prodotti complementari di altri brand non in concorrenza con la Casa Madre (es. tappeti, illuminazione, decor).
            </p>
          </div>
          {policyEnabled && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="add-ext-btn" className="h-10 px-4 inline-flex items-center gap-1.5 rounded-sm bg-brand text-white text-sm hover:bg-brand-hover">
                  <Plus className="w-4 h-4" /> Aggiungi prodotto
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-auto">
                <DialogHeader><DialogTitle>Nuovo prodotto complementare</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                      className="h-10 px-3 rounded-sm border border-border text-sm font-mono" data-testid="ext-sku" />
                    <input required placeholder="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                      className="h-10 px-3 rounded-sm border border-border text-sm" data-testid="ext-brand" />
                  </div>
                  <input required placeholder="Nome prodotto" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    className="w-full h-10 px-3 rounded-sm border border-border text-sm" data-testid="ext-nome" />
                  <input required placeholder="Categoria (es. tappeti, illuminazione)" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                    className="w-full h-10 px-3 rounded-sm border border-border text-sm" data-testid="ext-cat" />
                  <textarea placeholder="Descrizione breve" value={form.descrizione_short} onChange={(e) => setForm({ ...form, descrizione_short: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-border text-sm" rows={2} />
                  <div className="grid grid-cols-2 gap-3">
                    <input required type="number" step="0.01" placeholder="Prezzo" value={form.prezzo} onChange={(e) => setForm({ ...form, prezzo: parseFloat(e.target.value) || 0 })}
                      className="h-10 px-3 rounded-sm border border-border text-sm" data-testid="ext-prezzo" />
                    <input type="number" placeholder="Stock" value={form.stock_qta} onChange={(e) => setForm({ ...form, stock_qta: parseInt(e.target.value) || 0 })}
                      className="h-10 px-3 rounded-sm border border-border text-sm" data-testid="ext-stock" />
                  </div>
                  <ImageUploader value="" onChange={(u) => u && setForm((f) => ({ ...f, immagini: [...f.immagini, u] }))} label="Aggiungi immagine" />
                  {form.immagini.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {form.immagini.map((u, i) => (
                        <div key={i} className="relative w-16 h-16 rounded-sm overflow-hidden border border-border">
                          <img src={u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setForm({ ...form, immagini: form.immagini.filter((_, idx) => idx !== i) })} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-foreground text-white text-xs">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="submit" data-testid="ext-submit" className="w-full h-11 rounded-sm bg-brand text-white text-sm">Crea prodotto</button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {!policyEnabled && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-3 mb-6" data-testid="policy-disabled-banner">
            <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-900">Funzione non abilitata</div>
              <div className="text-xs text-amber-800 mt-1">La tua Casa Madre non ha (ancora) abilitato l&apos;inserimento di prodotti esterni. Contattali per richiederlo.</div>
            </div>
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-4 gap-3 mb-8" data-testid="billing-summary">
            <Stat label="Prodotti totali" value={summary.count_products} />
            <Stat label="Inclusi gratuiti" value={summary.free_included} />
            <Stat label="Oltre quota" value={summary.extra_products} />
            <Stat label="Costo mensile" value={`€ ${summary.monthly_fee.toFixed(2)}`} highlight />
          </div>
        )}

        <div className="space-y-2">
          {items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-sm">
              <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">Nessun prodotto esterno</p>
            </div>
          ) : items.map((it) => (
            <div key={it.id} className="p-4 border border-border rounded-sm bg-white flex items-center gap-4" data-testid={`ext-item-${it.sku}`}>
              <div className="w-14 h-14 bg-neutral-100 rounded-sm overflow-hidden">
                {it.immagini?.[0] && <img src={it.immagini[0]} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.nome}</div>
                <div className="text-xs text-muted-foreground">{it.brand} · {it.categoria} · {it.sku}</div>
              </div>
              <div className="text-sm font-medium">€ {it.prezzo}</div>
              <span className={`px-2 py-0.5 text-xs rounded-sm ${it.approval_status === "approved" ? "bg-emerald-100 text-emerald-800" : it.approval_status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                {it.approval_status}
              </span>
              <button onClick={() => remove(it.id)} className="text-red-500 hover:bg-red-50 w-8 h-8 rounded-sm" data-testid={`del-ext-${it.sku}`}><Trash2 className="w-4 h-4 mx-auto" /></button>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`p-4 rounded-sm border ${highlight ? "border-brand bg-brand text-white" : "border-border bg-white"}`}>
    <div className={`overline ${highlight ? "text-white/70" : "text-muted-foreground"} mb-1`}>{label}</div>
    <div className={`font-heading font-bold text-2xl ${highlight ? "" : ""}`}>{value}</div>
  </div>
);
